import { useState } from 'react'

import cn from 'clsx'
import { useDispatch } from 'react-redux'

import { getValueWithCurrency } from '@App/helpers/toCurrency'
import { useAppSelector } from '@App/hooks'
import { BalanceIndicatorKey, useAllWalletsBalanceIndicators } from '@App/hooks/useBalanceInfo'
import { Wallet } from '@App/interfaces/CommonInterfaces'
import { MODAL_TYPES } from '@App/redux/slices/modals/enums'
import { setModal } from '@App/redux/slices/modals/ModalSlice'
import { currentWallet, allWallets } from '@App/redux/slices/user/selectors'
import { setCurrWallet } from '@App/redux/slices/user/UserSlice'
import { CheckIcon } from '@App/static/icons/FooterIcons'
import { PlusWithBGIcon } from '@App/static/icons/SpotOrderIcons'

import { Button } from '../../controls/Button'
import { ModalTypes } from '../modalTypes'

import s from './styles.module.scss'

enum Tabs {
  LiveAcconts = 1,
  DemoAccount = 2,
}

export const SwitchAccountModal = ({ isOpen, setIsOpen }: ModalTypes) => {
  const dispatch = useDispatch()

  const currWallet = useAppSelector(currentWallet)
  const wallets = useAppSelector((state) => allWallets(state))
  const walletsBalanceIndicators = useAllWalletsBalanceIndicators()

  const handleAddAccount = () => {
    dispatch(setModal({ modalType: MODAL_TYPES.CHOOSE_ACCOUNT_CURRENCY_MODAL }))
  }
  const [activeTab, setActiveTab] = useState(Tabs.LiveAcconts)
  const handleOverlayClick = () => {
    setIsOpen()
  }

  const setCurrentWallet = (id: string) => {
    dispatch(setCurrWallet(id))
    setIsOpen()
  }

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }

  return (
    <div className={s.overlay} onClick={handleOverlayClick}>
      <div className={s.container} onClick={handleContentClick}>
        <div className={s.header}>
          <div className={s.tabButtons}>
            <div
              key={Tabs.LiveAcconts}
              onClick={() => setActiveTab(Tabs.LiveAcconts)}
              className={cn(s.tabButton, activeTab === Tabs.LiveAcconts && s.active)}
            >
              Live accounts
            </div>
            <div
              key={Tabs.DemoAccount}
              onClick={() => setActiveTab(Tabs.DemoAccount)}
              className={cn(s.tabButton, activeTab === Tabs.DemoAccount && s.active)}
            >
              Demo accounts
            </div>
          </div>
          <hr />
        </div>
        <div className={s.tabs}>
          {activeTab === Tabs.LiveAcconts && (
            <>
              <div className={s.tab}>
                {wallets.map(
                  (wallet) =>
                    walletsBalanceIndicators[Number(wallet.id)] && (
                      <AccountWallet
                        key={wallet.id}
                        wallet={wallet}
                        isActive={wallet.id === currWallet?.id}
                        indicators={walletsBalanceIndicators[Number(wallet.id)]}
                        accontPrefix="Live"
                        onClick={setCurrentWallet}
                      />
                    ),
                )}
              </div>
              <div className={s.footer}>
                <Button className={cn(s.btn, s.outlined, s.small)} onClick={handleAddAccount}>
                  <>
                    <PlusWithBGIcon className={s.plusIcon} /> Add Live account
                  </>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
const AccountWallet = ({
  wallet,
  indicators,
  isActive,
  accontPrefix,
  onClick,
}: {
  wallet: Wallet
  accontPrefix?: string
  isActive?: boolean
  indicators: {
    key: BalanceIndicatorKey
    value: number
  }[]
  onClick?: (id: string) => void
}) => {
  const pnl = indicators.find((b) => b.key === 'pnl')?.value || 0
  return (
    <div key={wallet.id} className={cn(s.wallet, isActive && s.active)} onClick={() => onClick?.(wallet.id)}>
      <span className={s.name}>
        <span className={s.type}>{accontPrefix} - </span>
        {wallet.name ? wallet.name : wallet.currency_code}
      </span>
      <span className={cn(s.pnl, pnl && (pnl > 0 ? s.blue : s.red))}>
        {getValueWithCurrency(wallet.currency_code, pnl)}
      </span>
      <span className={s.amount}>{getValueWithCurrency(wallet.currency_code, wallet.amount)}</span>
      <CheckIcon className={s.checkIcon} />
    </div>
  )
}
